package com.havn.app.features.openhouses.presentation

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnLeadCard
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonBlock
import com.havn.app.core.designsystem.HavnSkeletonListFooter
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatCard
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.designsystem.HavnWebRefreshAction
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.VisitorKioskShell
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnAsyncImage
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnAgencyMediaPreviewSource
import com.havn.app.core.media.havnPropertyPhotoPreviewSource
import com.havn.app.core.platform.HavnBackBlocker
import com.havn.app.core.platform.HavnCameraFacing
import com.havn.app.core.platform.HavnKioskSystemBars
import com.havn.app.core.platform.HavnQrScannerPreview
import com.havn.app.core.platform.playHavnKioskRecognitionBell
import com.havn.app.core.qr.HavnQrCode
import com.havn.app.features.clients.domain.HavnClientQrPayload
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHousePropertyOption
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.openhouses.domain.OpenHouseVisitor
import com.havn.app.features.openhouses.domain.VisitorInterest
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.presentation.LinkedTasksSection
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlinx.coroutines.delay

data class KioskBranding(
    val agencyName: String? = null,
    val agencyIconUrl: String? = null,
    val agentName: String? = null,
    val useAgencyBranding: Boolean = false
) {
    val title: String
        get() = agencyName?.takeIf { useAgencyBranding && it.isNotBlank() } ?: "HAVN"

    val subtitle: String
        get() = agentName?.takeIf { useAgencyBranding && it.isNotBlank() } ?: "OPEN HOUSES. ELEVATED."
}

@Composable
fun OpenHousesScreen(
    viewModel: OpenHousesViewModel,
    kioskPin: String = "1234",
    kioskBranding: KioskBranding = KioskBranding(),
    returnToToday: Boolean = false,
    returnToContactLabel: String? = null,
    returnToTasks: Boolean = false,
    returnToTasksLabel: String? = null,
    onReturnToToday: () -> Unit = {},
    onReturnToProperty: (String) -> Unit = {},
    onReturnToContact: () -> Unit = {},
    onReturnToTasks: () -> Unit = {},
    onOpenLead: (String, String) -> Unit = { _, _ -> },
    linkedTasks: List<HavnTask> = emptyList(),
    onOpenTask: (String) -> Unit = {},
    onUpgradePlan: () -> Unit = {},
    scrollToTopSignal: Int = 0
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val returnFromCreateOpening: () -> Unit = {
        val propertyId = state.returnToPropertyId
        if (propertyId == null) {
            viewModel.showList()
        } else {
            viewModel.clearPropertyReturn()
            onReturnToProperty(propertyId)
        }
    }

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = OpenHousesMode::navigationDepth
    ) { mode ->
        if (mode == OpenHousesMode.VisitorKiosk) {
            state.selectedOpenHouse?.let {
                VisitorKioskModeScreen(
                    openHouse = it,
                    step = state.kioskStep,
                    form = state.visitorForm,
                    error = state.kioskError,
                    isSaving = state.isSaving,
                    kioskPin = kioskPin,
                    branding = kioskBranding,
                    onChange = viewModel::updateVisitorForm,
                    scanMessage = state.qrScanMessage,
                    recognitionFlashKey = state.qrRecognitionEventId,
                    onQrScanned = viewModel::checkInScannedQrPayload,
                    onScannerError = { viewModel.updateQrScanPayload("") },
                    onNext = viewModel::nextKioskStep,
                    onBack = viewModel::previousKioskStep,
                    onSubmit = viewModel::submitVisitorSignIn,
                    onExit = viewModel::exitKioskMode
                )
            }
        } else {
        HavnScreenFrame(
            eyebrow = if (
                mode == OpenHousesMode.Detail ||
                mode == OpenHousesMode.Edit ||
                mode == OpenHousesMode.Schedule ||
                mode == OpenHousesMode.ScanQr ||
                mode == OpenHousesMode.SelectClients ||
                mode == OpenHousesMode.VisitorKiosk
            ) "" else strings.openHousesEyebrow,
            title = when (mode) {
                OpenHousesMode.List -> strings.navOpenHouses
                OpenHousesMode.Detail -> strings.openingDetails
                OpenHousesMode.Schedule -> strings.scheduleOpenHouse
                OpenHousesMode.Edit -> strings.editOpening
                OpenHousesMode.LiveDashboard -> strings.liveDashboard
                OpenHousesMode.VisitorKiosk -> strings.visitorKiosk
                OpenHousesMode.ScanQr -> strings.addQrClient
                OpenHousesMode.SelectClients -> strings.addManually
            },
            subtitle = if (
                mode == OpenHousesMode.Detail ||
                mode == OpenHousesMode.Schedule ||
                mode == OpenHousesMode.Edit ||
                mode == OpenHousesMode.ScanQr ||
                mode == OpenHousesMode.SelectClients ||
                mode == OpenHousesMode.VisitorKiosk
            ) "" else strings.openHousesSubtitle,
            metrics = if (mode == OpenHousesMode.List || mode == OpenHousesMode.Schedule || mode == OpenHousesMode.Edit || mode == OpenHousesMode.Detail || mode == OpenHousesMode.ScanQr || mode == OpenHousesMode.SelectClients || mode == OpenHousesMode.VisitorKiosk) {
                emptyList()
            } else {
                listOf(
                    HavnMetric(strings.liveMetric, state.liveCount.toString(), strings.inProgressMetric),
                    HavnMetric(strings.upcomingMetric, state.scheduledCount.toString(), strings.scheduledMetric),
                    HavnMetric(strings.commonVisitors, state.visitorCount.toString(), strings.capturedMetric)
                )
            },
            backLabel = when (mode) {
                OpenHousesMode.List,
                OpenHousesMode.VisitorKiosk -> null
                OpenHousesMode.Detail,
                OpenHousesMode.Schedule -> when {
                    returnToTasks -> returnToTasksLabel ?: strings.taskDetail
                    state.returnToPropertyId != null -> strings.commonPropertyName
                    returnToContactLabel != null -> returnToContactLabel
                    returnToToday -> strings.commonToday
                    else -> strings.navOpenHouses
                }
                OpenHousesMode.Edit -> state.selectedOpenHouse?.displayTitle ?: strings.commonOpenHouse
                OpenHousesMode.LiveDashboard -> state.selectedOpenHouse?.displayTitle ?: strings.commonOpenHouse
                OpenHousesMode.ScanQr -> state.selectedOpenHouse?.displayTitle ?: strings.commonOpenHouse
                OpenHousesMode.SelectClients -> state.selectedOpenHouse?.displayTitle ?: strings.commonOpenHouse
            },
            onBack = when (mode) {
                OpenHousesMode.List,
                OpenHousesMode.VisitorKiosk -> null
                OpenHousesMode.Detail -> when {
                    returnToTasks -> onReturnToTasks
                    state.returnToPropertyId != null -> returnFromCreateOpening
                    returnToContactLabel != null -> onReturnToContact
                    returnToToday -> onReturnToToday
                    else -> viewModel::showList
                }
                OpenHousesMode.Schedule -> returnFromCreateOpening
                OpenHousesMode.Edit -> viewModel::cancelForm
                OpenHousesMode.LiveDashboard -> {
                    val openHouse = state.selectedOpenHouse
                    if (openHouse == null) null else ({ viewModel.openDetail(openHouse.id) })
                }
                OpenHousesMode.ScanQr -> {
                    val openHouse = state.selectedOpenHouse
                    if (openHouse == null) null else ({ viewModel.openDetail(openHouse.id) })
                }
                OpenHousesMode.SelectClients -> {
                    val openHouse = state.selectedOpenHouse
                    if (openHouse == null) null else ({ viewModel.openDetail(openHouse.id) })
                }
            },
            isRefreshing = state.isLoading,
            onRefresh = viewModel::refresh,
            compactHeader = mode == OpenHousesMode.List,
            scrollToTopSignal = if (mode == OpenHousesMode.List) scrollToTopSignal else 0,
            onScrolledNearBottom = if (
                mode == OpenHousesMode.List &&
                state.hasMoreOpenHouses &&
                !state.isLoading &&
                !state.isLoadingMore
            ) {
                viewModel::loadMoreOpenHouses
            } else if (
                mode == OpenHousesMode.SelectClients &&
                state.hasMoreClientSelectorClients &&
                !state.isLoadingClientSelector &&
                !state.isLoadingMoreClientSelector
            ) {
                viewModel::loadMoreClientSelectorClients
            } else null,
            headerTrailing = state.selectedOpenHouse?.takeIf { mode == OpenHousesMode.Detail }?.let { openHouse ->
                {
                    HavnWebRefreshAction(
                        onRefresh = { viewModel.openDetail(openHouse.id) },
                        isRefreshing = state.isDetailLoading
                    )
                }
            }
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                state.error?.let {
                    ErrorPanel(it, onDismiss = viewModel::clearError, onUpgradePlan = onUpgradePlan)
                }

                when (mode) {
                    OpenHousesMode.List -> OpenHouseListScreen(
                        state = state,
                        onOpen = viewModel::openDetail,
                        onOpenLead = onOpenLead,
                        onSearchChanged = viewModel::updateSearchQuery,
                        onLoadMore = viewModel::loadMoreOpenHouses
                    )

                    OpenHousesMode.Detail -> state.selectedOpenHouse?.let {
                        OpenHouseDetailScreen(
                        openHouse = it,
                        kioskCode = state.kioskSession?.takeIf { session -> session.openHouseId == it.id }?.code,
                        kioskPath = state.kioskSession?.takeIf { session -> session.openHouseId == it.id }?.kioskPath,
                        isSaving = state.isSaving,
                        isDetailLoading = state.isDetailLoading,
                        onBack = when {
                            returnToTasks -> onReturnToTasks
                            state.returnToPropertyId != null -> returnFromCreateOpening
                            returnToContactLabel != null -> onReturnToContact
                            returnToToday -> onReturnToToday
                            else -> viewModel::showList
                        },
                        onEdit = viewModel::startEdit,
                        onDelete = viewModel::requestDeleteSelected,
                        onKiosk = viewModel::generateKioskSession,
                        onLaunchKiosk = viewModel::launchKioskMode,
                        onScanQr = viewModel::showQrScanner,
                        onSelectClients = viewModel::showClientSelector,
                        onOpenLead = { leadId -> onOpenLead(leadId, it.id) },
                        onRemoveLead = viewModel::requestRemoveClient,
                        linkedTasks = linkedTasks.filter { task -> task.openHouseId == it.id },
                        onOpenTask = onOpenTask
                        )
                    }

                    OpenHousesMode.Schedule -> OpeningFormScreen(
                    state = state.form,
                    properties = state.propertyOptions,
                    isSaving = state.isSaving,
                    onChange = viewModel::updateForm,
                    onCancel = returnFromCreateOpening,
                    submitText = strings.scheduleOpenHouse,
                    onSubmit = viewModel::scheduleOpenHouse,
                    lockPropertySelection = state.returnToPropertyId != null,
                    showCancel = false
                    )

                    OpenHousesMode.Edit -> OpeningFormScreen(
                    state = state.form,
                    properties = state.propertyOptions,
                    lockedProperty = state.selectedOpenHouse?.toPropertyOption(),
                    isSaving = state.isSaving,
                    onChange = viewModel::updateForm,
                    onCancel = viewModel::cancelForm,
                    submitText = strings.commonSave,
                    onSubmit = viewModel::saveEditedOpenHouse,
                    onDelete = viewModel::requestDeleteSelected,
                    showCancel = false,
                    lockPropertySelection = true
                    )

                    OpenHousesMode.LiveDashboard -> state.selectedOpenHouse?.let {
                        LiveOpenHouseDashboardScreen(
                            openHouse = it,
                            isSaving = state.isSaving,
                            onBack = { viewModel.openDetail(it.id) },
                            onEnd = viewModel::endSelectedOpenHouse,
                        onKiosk = viewModel::generateKioskSession,
                        onLaunchKiosk = viewModel::launchKioskMode,
                        onScanQr = viewModel::showQrScanner,
                        onSelectClients = viewModel::showClientSelector,
                        kioskCode = state.kioskSession?.takeIf { session -> session.openHouseId == it.id }?.code,
                        onOpenLead = { leadId -> onOpenLead(leadId, it.id) },
                        onRemoveLead = viewModel::requestRemoveClient
                        )
                    }

                    OpenHousesMode.VisitorKiosk -> state.selectedOpenHouse?.let {
                        VisitorKioskModeScreen(
                        openHouse = it,
                        step = state.kioskStep,
                        form = state.visitorForm,
                        error = state.kioskError,
                        isSaving = state.isSaving,
                        kioskPin = kioskPin,
                        branding = kioskBranding,
                        onChange = viewModel::updateVisitorForm,
                        scanMessage = state.qrScanMessage,
                        recognitionFlashKey = state.qrRecognitionEventId,
                        onQrScanned = viewModel::checkInScannedQrPayload,
                        onScannerError = { viewModel.updateQrScanPayload("") },
                        onNext = viewModel::nextKioskStep,
                        onBack = viewModel::previousKioskStep,
                        onSubmit = viewModel::submitVisitorSignIn,
                        onExit = viewModel::exitKioskMode
                        )
                    }

                    OpenHousesMode.ScanQr -> state.selectedOpenHouse?.let {
                        ScanClientQrScreen(
                            openHouse = it,
                            payload = state.qrScanPayload,
                            payloads = state.qrScanPayloads,
                            message = state.qrScanMessage,
                            error = state.kioskError,
                            isSaving = state.isSaving,
                            onPayloadChange = viewModel::updateQrScanPayload,
                            onPayloadScanned = viewModel::submitScannedQrPayload,
                            onRemovePayload = viewModel::removeScannedQrPayload,
                            onSubmit = viewModel::submitQrScan,
                            onBack = { viewModel.openDetail(it.id) }
                        )
                    }

                    OpenHousesMode.SelectClients -> state.selectedOpenHouse?.let {
                        SelectClientsScreen(
                            openHouse = it,
                            clients = state.filteredClients,
                            clientSearchQuery = state.clientSearchQuery,
                            message = state.qrScanMessage,
                            error = state.kioskError,
                            isSaving = state.isSaving,
                            isLoadingClients = state.isLoadingClientSelector,
                            isLoadingMoreClients = state.isLoadingMoreClientSelector,
                            selectedTemperature = state.manualLeadTemperature,
                            onClientSearchChange = viewModel::updateClientSearchQuery,
                            onTemperatureSelected = viewModel::updateManualLeadTemperature,
                            onClientSelected = viewModel::checkInClient,
                        )
                    }
                }
            }
        }
        }
    }

    if (state.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = viewModel::dismissDeleteConfirmation,
            title = { Text(strings.deleteOpening) },
            text = { Text(strings.deleteOpeningBody) },
            confirmButton = {
                TextButton(onClick = viewModel::confirmDeleteSelected) {
                    Text(strings.commonDelete, color = HavnDanger)
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissDeleteConfirmation) {
                    Text(strings.commonCancel)
                }
            }
        )
    }

    if (state.pendingRemoveLeadClientId != null) {
        val leadName = state.pendingRemoveLeadName?.takeIf { it.isNotBlank() } ?: strings.removeLeadFallback
        AlertDialog(
            onDismissRequest = viewModel::dismissRemoveLeadConfirmation,
            title = { Text(strings.removeLeadTitle) },
            text = { Text(strings.removeLeadBody.replace("%s", leadName)) },
            confirmButton = {
                TextButton(onClick = viewModel::confirmRemoveLead) {
                    Text(strings.commonDelete, color = HavnDanger)
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissRemoveLeadConfirmation) {
                    Text(strings.commonCancel)
                }
            }
        )
    }
}

@Composable
fun OpenHouseListScreen(
    state: OpenHousesUiState,
    onOpen: (String) -> Unit,
    onOpenLead: (String, String) -> Unit,
    onSearchChanged: (String) -> Unit,
    onLoadMore: () -> Unit
) {
    val strings = LocalHavnStrings.current
    var selectedFilter by remember { mutableStateOf(OpeningListFilter.All) }
    val filteredOpenings = state.openHouses.filter { opening ->
        val timelineStatus = opening.timelineStatus()
        val matchesStatus = when (selectedFilter) {
            OpeningListFilter.All -> true
            OpeningListFilter.Scheduled -> timelineStatus == OpeningTimelineStatus.Planned
            OpeningListFilter.Active -> timelineStatus == OpeningTimelineStatus.Active
            OpeningListFilter.Past -> timelineStatus == OpeningTimelineStatus.Archived
        }
        matchesStatus
    }

    Column(verticalArrangement = Arrangement.spacedBy(HavnListDefaults.searchToFilterGap)) {
        HavnSearchField(
            value = state.searchQuery,
            onValueChange = onSearchChanged,
            placeholder = strings.searchOpenings
        )

        OpeningStatusSegments(selected = selectedFilter, onSelected = { selectedFilter = it })

        when {
            (!state.hasLoadedInitialPage || state.isLoading) && state.openHouses.isEmpty() -> {
                repeat(6) {
                    OpeningCardSkeleton()
                }
            }
            state.propertyOptions.isEmpty() -> {
                EmptyState(
                    title = strings.addPropertyFirstTitle,
                    body = strings.addPropertyFirstBody,
                    actionText = null,
                    onAction = null
                )
            }
            state.openHouses.isEmpty() -> {
                EmptyState(
                    title = strings.noOpenHousesTitle,
                    body = strings.noOpenHousesBody,
                    actionText = null,
                    onAction = null
                )
            }
            filteredOpenings.isEmpty() -> {
                EmptyState(
                    title = strings.noOpeningsFoundTitle,
                    body = strings.noOpeningsFoundBody,
                    actionText = null,
                    onAction = null
                )
            }
            else -> {
                filteredOpenings.forEach { openHouse ->
                    OpeningListCard(
                        openHouse = openHouse,
                        onOpen = { onOpen(openHouse.id) }
                    )
                }
                if (state.hasMoreOpenHouses) {
                    HavnSkeletonListFooter()
                }
            }
        }
    }
}

@Composable
private fun OpeningCardSkeleton() {
    HavnCard(
        modifier = Modifier.fillMaxWidth(),
        border = BorderStroke(1.dp, HavnCardBorder)
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SkeletonBlock(Modifier.size(54.dp).clip(MaterialTheme.shapes.medium))
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                SkeletonBlock(Modifier.fillMaxWidth(0.54f).height(15.dp))
                SkeletonBlock(Modifier.fillMaxWidth(0.76f).height(12.dp))
                SkeletonBlock(Modifier.fillMaxWidth(0.38f).height(12.dp))
            }
            SkeletonBlock(Modifier.width(64.dp).height(24.dp))
        }
    }
}

@Composable
private fun SkeletonBlock(modifier: Modifier = Modifier) {
    HavnSkeletonBlock(modifier, shape = MaterialTheme.shapes.small)
}

private enum class OpeningListFilter {
    All,
    Scheduled,
    Active,
    Past
}

@Composable
private fun OpeningStatusSegments(
    selected: OpeningListFilter,
    onSelected: (OpeningListFilter) -> Unit
) {
    val strings = LocalHavnStrings.current
    val segments = listOf(
        OpeningListFilter.All to strings.commonAll,
        OpeningListFilter.Active to strings.commonActive,
        OpeningListFilter.Scheduled to strings.commonPlanned,
        OpeningListFilter.Past to strings.commonPast
    )

    Row(
        horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
        modifier = Modifier.fillMaxWidth()
    ) {
        segments.forEach { (filter, label) ->
            HavnListFilterChip(
                label = label,
                selected = selected == filter,
                onClick = { onSelected(filter) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
fun OpenHouseDetailScreen(
    openHouse: OpenHouse,
    kioskCode: String?,
    kioskPath: String?,
    isSaving: Boolean,
    isDetailLoading: Boolean,
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onKiosk: () -> Unit,
    onLaunchKiosk: () -> Unit,
    onScanQr: () -> Unit,
    onSelectClients: () -> Unit,
    onOpenLead: (String) -> Unit,
    onRemoveLead: (String) -> Unit,
    linkedTasks: List<HavnTask>,
    onOpenTask: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val wide = havnIsWebShell && maxWidth >= 960.dp
        if (wide) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.lg),
                verticalAlignment = Alignment.Top
            ) {
                OpenHouseSummary(openHouse, modifier = Modifier.weight(1.45f))
                Column(
                    modifier = Modifier.widthIn(max = 440.dp).weight(1f),
                    verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)
                ) {
                    OpeningLeadsCard(
                        openHouse = openHouse,
                        isLoading = isDetailLoading,
                        onOpenKiosk = onLaunchKiosk,
                        onAddQrClient = onScanQr,
                        onSelectClients = onSelectClients,
                        onOpenLead = onOpenLead,
                        onRemoveLead = onRemoveLead,
                        isSaving = isSaving
                    )
                    LinkedTasksSection(
                        tasks = linkedTasks,
                        onOpenTask = onOpenTask
                    )
                    OpeningDetailActions(
                        strings = strings,
                        isSaving = isSaving,
                        onEdit = onEdit,
                        onDelete = onDelete
                    )
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                OpenHouseSummary(openHouse)
                OpeningLeadsCard(
                    openHouse = openHouse,
                    isLoading = isDetailLoading,
                    onOpenKiosk = onLaunchKiosk,
                    onAddQrClient = onScanQr,
                    onSelectClients = onSelectClients,
                    onOpenLead = onOpenLead,
                    onRemoveLead = onRemoveLead,
                    isSaving = isSaving
                )
                LinkedTasksSection(
                    tasks = linkedTasks,
                    onOpenTask = onOpenTask
                )
                OpeningDetailActions(
                    strings = strings,
                    isSaving = isSaving,
                    onEdit = onEdit,
                    onDelete = onDelete
                )
            }
        }
    }
}

@Composable
private fun OpeningDetailActions(
    strings: com.havn.app.core.localization.HavnStrings,
    isSaving: Boolean,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    HavnButton(
        text = strings.editOpening,
        onClick = onEdit,
        modifier = Modifier.fillMaxWidth(),
        style = HavnButtonStyle.Secondary
    )
    HavnButton(
        text = strings.deleteOpening,
        onClick = onDelete,
        modifier = Modifier.fillMaxWidth(),
        style = HavnButtonStyle.Danger,
        loading = isSaving
    )
}

@Composable
fun OpeningFormScreen(
    state: OpenHouseFormState,
    properties: List<OpenHousePropertyOption>,
    lockedProperty: OpenHousePropertyOption? = null,
    isSaving: Boolean,
    onChange: (OpenHouseFormState.() -> OpenHouseFormState) -> Unit,
    onCancel: () -> Unit,
    submitText: String,
    onSubmit: () -> Unit,
    onDelete: (() -> Unit)? = null,
    showCancel: Boolean = true,
    lockPropertySelection: Boolean = false
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var dateTimePickerTarget by remember { mutableStateOf<DateTimeTarget?>(null) }
    var showInternalNotes by remember(state.internalNotes) { mutableStateOf(state.internalNotes.isNotBlank()) }
    val selectedProperty = properties.firstOrNull { it.id == state.propertyId }
        ?: lockedProperty?.takeIf { it.id == state.propertyId }
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            state.validationError?.let {
                Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.bodyMedium)
            }
            if (lockPropertySelection) {
                selectedProperty?.let { property ->
                    FixedOpeningPropertyCard(property)
                } ?: Text(
                    text = strings.propertyUnavailableTitle,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyLarge
                )
            } else {
                Text(strings.commonProperty, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                properties.forEach { property ->
                    HavnButton(
                        text = property.title,
                        onClick = { onChange { copy(propertyId = property.id, validationError = null) } },
                        modifier = Modifier.fillMaxWidth(),
                        style = if (state.propertyId == property.id) HavnButtonStyle.Primary else HavnButtonStyle.Secondary
                    )
                }
            }
            HavnTextField(state.title, { onChange { copy(title = it) } }, strings.openingName)
            DateTimePickerField(
                label = strings.startsAt,
                value = state.startsAt,
                placeholder = strings.chooseStartDateTime,
                onClick = { dateTimePickerTarget = DateTimeTarget.Start }
            )
            DateTimePickerField(
                label = strings.endsAt,
                value = state.endsAt,
                placeholder = strings.chooseEndDateTime,
                onClick = { dateTimePickerTarget = DateTimeTarget.End }
            )
            HavnTextField(state.accessNotes, { onChange { copy(accessNotes = it) } }, strings.accessNotes, singleLine = false)
            if (showInternalNotes) {
                HavnTextField(state.internalNotes, { onChange { copy(internalNotes = it) } }, strings.privateNotes, singleLine = false)
            } else {
                HavnButton(
                    text = strings.addPrivateNote,
                    onClick = { showInternalNotes = true },
                    modifier = Modifier.fillMaxWidth(),
                    style = HavnButtonStyle.Ghost,
                    leadingIcon = HavnIconKey.Note
                )
            }
        }
    }
    if (showCancel) {
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnButton(strings.commonCancel, onClick = onCancel, modifier = Modifier.weight(1f), style = HavnButtonStyle.Ghost)
            HavnButton(submitText, onClick = onSubmit, modifier = Modifier.weight(1f), loading = isSaving)
        }
    } else {
        HavnButton(submitText, onClick = onSubmit, modifier = Modifier.fillMaxWidth(), loading = isSaving)
    }

    if (onDelete != null) {
        HavnButton(
            text = strings.deleteOpening,
            onClick = onDelete,
            modifier = Modifier.fillMaxWidth(),
            style = HavnButtonStyle.Danger,
            enabled = !isSaving,
            leadingIcon = HavnIconKey.Trash
        )
    }

    dateTimePickerTarget?.let { target ->
        HavnDateTimePickerDialog(
            title = if (target == DateTimeTarget.Start) strings.startsAt else strings.endsAt,
            initialValue = if (target == DateTimeTarget.Start) state.startsAt else state.endsAt,
            onDismiss = { dateTimePickerTarget = null },
            onConfirm = { selectedValue ->
                onChange {
                    if (target == DateTimeTarget.Start) {
                        copy(startsAt = selectedValue, validationError = null)
                    } else {
                        copy(endsAt = selectedValue, validationError = null)
                    }
                }
                dateTimePickerTarget = null
            }
        )
    }
}

@Composable
private fun FixedOpeningPropertyCard(property: OpenHousePropertyOption) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = theme.background.copy(alpha = 0.34f),
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(HavnSpacing.sm),
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(64.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .background(theme.surface),
                contentAlignment = Alignment.Center
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = property.thumbnailDataUrl,
                    previewDataUrl = havnPropertyPhotoPreviewSource(property.thumbnailDataUrl, 120),
                    contentDescription = property.title,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(
                        icon = HavnIconKey.Properties,
                        contentDescription = null,
                        tint = theme.primaryAccent,
                        modifier = Modifier.size(22.dp)
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs), modifier = Modifier.weight(1f)) {
                Text(
                    property.title,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    property.address,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

private enum class DateTimeTarget {
    Start,
    End
}

@Composable
private fun DateTimePickerField(
    label: String,
    value: String,
    placeholder: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)) {
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Surface(
            modifier = Modifier.fillMaxWidth(),
            onClick = onClick,
            shape = MaterialTheme.shapes.medium,
            color = theme.background.copy(alpha = 0.34f),
            contentColor = theme.primaryText,
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.44f))
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 54.dp)
                    .padding(horizontal = HavnSpacing.md, vertical = HavnSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
            ) {
                HavnIcon(HavnIconKey.Calendar, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
                Text(
                    text = value.takeIf { it.isNotBlank() }?.toReadableDateTime() ?: placeholder,
                    color = if (value.isBlank()) theme.mutedText else theme.primaryText,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun HavnDateTimePickerDialog(
    title: String,
    initialValue: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val initial = remember(initialValue) { DateTimeSelection.fromIso(initialValue) }
    var year by remember(initialValue) { mutableStateOf(initial.year) }
    var month by remember(initialValue) { mutableStateOf(initial.month) }
    var day by remember(initialValue) { mutableStateOf(initial.day.coerceAtMost(daysInMonth(initial.year, initial.month))) }
    var hour by remember(initialValue) { mutableStateOf(initial.hour) }
    var minute by remember(initialValue) { mutableStateOf(initial.minute) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CalendarControlButton("<", onClick = {
                        if (month == 1) {
                            month = 12
                            year -= 1
                        } else {
                            month -= 1
                        }
                        day = day.coerceAtMost(daysInMonth(year, month))
                    })
                    Text(
                        text = "${month.twoDigits()} / $year",
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleLarge,
                        textAlign = TextAlign.Center
                    )
                    CalendarControlButton(">", onClick = {
                        if (month == 12) {
                            month = 1
                            year += 1
                        } else {
                            month += 1
                        }
                        day = day.coerceAtMost(daysInMonth(year, month))
                    })
                }

                MonthGrid(
                    selectedDay = day,
                    daysInMonth = daysInMonth(year, month),
                    onDaySelected = { day = it }
                )

                Text(strings.commonTime, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
                Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                    StepperValue(
                        label = strings.commonHour,
                        value = hour.twoDigits(),
                        onDecrease = { hour = (hour + 23) % 24 },
                        onIncrease = { hour = (hour + 1) % 24 },
                        modifier = Modifier.weight(1f)
                    )
                    StepperValue(
                        label = strings.commonMinute,
                        value = minute.twoDigits(),
                        onDecrease = { minute = (minute + 45) % 60 },
                        onIncrease = { minute = (minute + 15) % 60 },
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        },
        confirmButton = {
            TextButton(onClick = { onConfirm(toIsoDateTime(year, month, day, hour, minute)) }) {
                Text(strings.commonDone)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel)
            }
        },
        containerColor = theme.surface,
        titleContentColor = theme.primaryText,
        textContentColor = theme.primaryText
    )
}

@Composable
private fun MonthGrid(
    selectedDay: Int,
    daysInMonth: Int,
    onDaySelected: (Int) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)) {
        (1..daysInMonth).chunked(7).forEach { week ->
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.xs), modifier = Modifier.fillMaxWidth()) {
                week.forEach { currentDay ->
                    val selected = selectedDay == currentDay
                    Surface(
                        modifier = Modifier.weight(1f),
                        onClick = { onDaySelected(currentDay) },
                        shape = MaterialTheme.shapes.small,
                        color = if (selected) theme.primaryAccent else theme.surface,
                        contentColor = if (selected) theme.background else theme.primaryText,
                        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
                    ) {
                        Box(
                            modifier = Modifier.height(42.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(currentDay.toString(), style = MaterialTheme.typography.labelLarge)
                        }
                    }
                }
                repeat(7 - week.size) {
                    Spacer(Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun StepperValue(
    label: String,
    value: String,
    onDecrease: () -> Unit,
    onIncrease: () -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(
            modifier = Modifier.padding(HavnSpacing.sm),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)
        ) {
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.xs), verticalAlignment = Alignment.CenterVertically) {
                CalendarControlButton("-", onClick = onDecrease, modifier = Modifier.size(44.dp))
                Text(value, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                CalendarControlButton("+", onClick = onIncrease, modifier = Modifier.size(44.dp))
            }
        }
    }
}

@Composable
private fun CalendarControlButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier.size(54.dp)
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        onClick = onClick,
        shape = MaterialTheme.shapes.medium,
        color = theme.background.copy(alpha = 0.46f),
        contentColor = theme.primaryText,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(text, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
        }
    }
}

private data class DateTimeSelection(
    val year: Int,
    val month: Int,
    val day: Int,
    val hour: Int,
    val minute: Int
) {
    companion object {
        fun fromIso(value: String): DateTimeSelection {
            val fallback = todaySelection()
            val date = value.substringBefore("T")
            val time = value.substringAfter("T", "18:00").substringBefore("Z").substringBefore("+").substringBefore(".")
            val parts = date.split("-")
            val timeParts = time.split(":")
            return DateTimeSelection(
                year = parts.getOrNull(0)?.toIntOrNull() ?: fallback.year,
                month = parts.getOrNull(1)?.toIntOrNull()?.coerceIn(1, 12) ?: fallback.month,
                day = parts.getOrNull(2)?.toIntOrNull()?.coerceIn(1, 31) ?: fallback.day,
                hour = timeParts.getOrNull(0)?.toIntOrNull()?.coerceIn(0, 23) ?: 18,
                minute = timeParts.getOrNull(1)?.toIntOrNull()?.coerceIn(0, 59) ?: 0
            )
        }

        @OptIn(ExperimentalTime::class)
        private fun todaySelection(): DateTimeSelection {
            val parts = Clock.System.now().toString().substringBefore("T").split("-")
            return DateTimeSelection(
                year = parts.getOrNull(0)?.toIntOrNull() ?: 2026,
                month = parts.getOrNull(1)?.toIntOrNull()?.coerceIn(1, 12) ?: 1,
                day = parts.getOrNull(2)?.toIntOrNull()?.coerceIn(1, 31) ?: 1,
                hour = 18,
                minute = 0
            )
        }
    }
}

private fun toIsoDateTime(year: Int, month: Int, day: Int, hour: Int, minute: Int): String =
    "${year}-${month.twoDigits()}-${day.twoDigits()}T${hour.twoDigits()}:${minute.twoDigits()}:00Z"

private fun String.toReadableDateTime(): String =
    readableOpenHouseDateTime()

private fun String.readableOpenHouseDateTime(): String {
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").removeSuffix("Z")
    val date = clean.substringBefore(" ").readableOpenHouseDate()
    val time = clean.substringAfter(" ", "").readableOpenHouseTime()
    return listOfNotNull(date, time).joinToString(" · ").ifBlank { clean }
}

private fun String.readableOpenHouseDate(): String? {
    val parts = split("-")
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull()?.let { monthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return "$month $day, ${parts[0]}"
}

private fun String.readableOpenHouseTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private fun OpenHouse.prettyTimeRange(): String =
    "${startsAt.prettyTime()} - ${endsAt.prettyTime()}"

private fun OpenHouse.prettyDateTimeRange(): String =
    "${startsAt.prettyDate()} · ${prettyTimeRange()}"

private fun String.prettyDate(): String {
    val date = substringBefore("T")
    val parts = date.split("-")
    if (parts.size != 3) return toReadableDateTime()
    val month = parts[1].toIntOrNull()?.let { monthNames.getOrNull(it - 1) } ?: return toReadableDateTime()
    val day = parts[2].toIntOrNull() ?: return toReadableDateTime()
    return "$month $day, ${parts[0]}"
}

private fun String.prettyTime(): String {
    val raw = substringAfter("T", this).substringBefore(".").substringBefore("+").substringBefore("Z")
    val parts = raw.split(":")
    val hour24 = parts.getOrNull(0)?.toIntOrNull() ?: return raw
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: 0
    val suffix = if (hour24 >= 12) "PM" else "AM"
    val hour12 = when (val normalized = hour24 % 12) {
        0 -> 12
        else -> normalized
    }
    return "$hour12:${minute.twoDigits()} $suffix"
}

private val monthNames = listOf(
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
)

private fun Int.twoDigits(): String = toString().padStart(2, '0')

private fun daysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (year.isLeapYear()) 29 else 28
    else -> 30
}

private fun Int.isLeapYear(): Boolean =
    this % 4 == 0 && (this % 100 != 0 || this % 400 == 0)

@Composable
fun LiveOpenHouseDashboardScreen(
    openHouse: OpenHouse,
    isSaving: Boolean,
    onBack: () -> Unit,
    onEnd: () -> Unit,
    onKiosk: () -> Unit,
    onLaunchKiosk: () -> Unit,
    onScanQr: () -> Unit,
    onSelectClients: () -> Unit,
    kioskCode: String?,
    onOpenLead: (String) -> Unit,
    onRemoveLead: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    OpenHouseSummary(openHouse)
    StatsGrid(openHouse)
    OpeningLeadsCard(
        openHouse = openHouse,
        isLoading = false,
        onOpenKiosk = onLaunchKiosk,
        onAddQrClient = onScanQr,
        onSelectClients = onSelectClients,
        onOpenLead = onOpenLead,
        onRemoveLead = onRemoveLead,
        isSaving = isSaving
    )
    HavnButton(strings.back, onClick = onBack, modifier = Modifier.fillMaxWidth(), style = HavnButtonStyle.Ghost)
    HavnButton(
        strings.endOpenHouse,
        onClick = onEnd,
        modifier = Modifier.fillMaxWidth(),
        style = HavnButtonStyle.Secondary,
        loading = isSaving,
        enabled = openHouse.status == OpenHouseStatus.Live
    )
}

@Composable
private fun OpeningListCard(openHouse: OpenHouse, onOpen: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        onClick = onOpen,
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.height(112.dp)
        ) {
            Box(
                modifier = Modifier
                    .width(124.dp)
                    .fillMaxHeight()
                    .clip(RoundedCornerShape(topStart = 24.dp, bottomStart = 24.dp))
            ) {
                OpeningPropertyImage(openHouse)
                OpeningStatusPill(
                    status = openHouse.timelineStatus(),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(7.dp)
                )
            }

            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .padding(start = 12.dp, top = 10.dp, end = 10.dp, bottom = 10.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(3.dp)) {
                    Text(
                        openHouse.displayTitle,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        openHouse.propertyAddress,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                Column(verticalArrangement = Arrangement.spacedBy(7.dp)) {
                    OpeningInfoLine(
                        icon = HavnIconKey.Calendar,
                        text = openHouse.prettyDateTimeRange()
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                        HavnIcon(HavnIconKey.Users, contentDescription = null, tint = theme.secondaryText, modifier = Modifier.size(15.dp))
                        Text(
                            text = "${openHouse.stats.visitorCount} ${strings.commonVisitors}",
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.labelMedium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = "+${openHouse.stats.hotCount} ${strings.commonHot}",
                            color = theme.primaryAccent,
                            style = MaterialTheme.typography.labelMedium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun BoxScope.OpeningPropertyImage(openHouse: OpenHouse) {
    val rendered = HavnAsyncImage(
        dataUrl = openHouse.propertyThumbnailDataUrl,
        contentDescription = openHouse.propertyTitle,
        modifier = Modifier.fillMaxSize(),
        contentScale = ContentScale.Crop
    )
    if (!rendered) {
        HavnSkeletonBlock(Modifier.fillMaxSize(), shape = MaterialTheme.shapes.small)
    }
}

@Composable
private fun OpeningInfoLine(
    icon: HavnIconKey,
    text: String
) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(7.dp), verticalAlignment = Alignment.CenterVertically) {
        HavnIcon(icon, contentDescription = null, tint = theme.secondaryText, modifier = Modifier.size(15.dp))
        Text(
            text = text,
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun OpeningStatusPill(
    status: OpeningTimelineStatus,
    modifier: Modifier = Modifier
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val (label, background) = when (status) {
        OpeningTimelineStatus.Active -> strings.commonActive.toOpeningBadgeLabel() to theme.success
        OpeningTimelineStatus.Planned -> strings.commonPlanned.toOpeningBadgeLabel() to theme.cold
        OpeningTimelineStatus.Archived -> strings.commonPast.toOpeningBadgeLabel() to theme.secondaryText.copy(alpha = 0.92f)
    }
    val content = background.readableBadgeTextColor()
    val labelStyle = MaterialTheme.typography.labelLarge.copy(
        fontSize = when {
            label.length > 10 -> 9.sp
            label.length > 8 -> 10.sp
            else -> MaterialTheme.typography.labelLarge.fontSize
        }
    )
    Surface(
        modifier = modifier.widthIn(max = 82.dp),
        color = background,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, background.copy(alpha = 0.95f))
    ) {
        Text(
            text = label,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
            color = content,
            style = labelStyle,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private fun String.toOpeningBadgeLabel(): String {
    val normalized = lowercase()
    return normalized.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
}

private fun Color.readableBadgeTextColor(): Color {
    val luminance = (0.299f * red) + (0.587f * green) + (0.114f * blue)
    return if (luminance > 0.58f) Color.Black else Color.White
}

private fun photoTextScrim(): Brush = Brush.verticalGradient(
    listOf(
        Color.Transparent,
        Color.Black.copy(alpha = 0.34f),
        Color.Black.copy(alpha = 0.78f)
    )
)

@Composable
private fun OpenHouseSummary(openHouse: OpenHouse, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(210.dp)
            ) {
                OpeningPropertyImage(openHouse)
                OpeningStatusPill(
                    status = openHouse.timelineStatus(),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(10.dp)
                )
                Column(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .fillMaxWidth()
                        .background(photoTextScrim())
                        .padding(14.dp)
                ) {
                    Text(
                        openHouse.displayTitle,
                        color = Color.White,
                        style = MaterialTheme.typography.headlineSmall,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        openHouse.propertyAddress,
                        color = Color.White.copy(alpha = 0.84f),
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }

            Column(
                modifier = Modifier.padding(horizontal = HavnSpacing.md),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    OpeningInfoLine(
                        icon = HavnIconKey.Calendar,
                        text = openHouse.startsAt.prettyDate()
                    )
                    OpeningInfoLine(
                        icon = HavnIconKey.Calendar,
                        text = openHouse.prettyTimeRange()
                    )
                }

                openHouse.accessNotes?.takeIf { it.isNotBlank() }?.let {
                    Text(it, color = theme.mutedText, style = MaterialTheme.typography.bodyMedium)
                }
            }
            Spacer(Modifier.height(HavnSpacing.md))
        }
    }
}

@Composable
private fun StatsGrid(openHouse: OpenHouse) {
    val strings = LocalHavnStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
        HavnStatCard(openHouse.stats.visitorCount.toString(), strings.commonVisitors, modifier = Modifier.weight(1f), icon = HavnIconKey.Users)
        HavnStatCard(openHouse.stats.hotCount.toString(), strings.commonHot, modifier = Modifier.weight(1f), icon = HavnIconKey.TrendUp)
    }
    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
        HavnStatCard(openHouse.stats.warmCount.toString(), strings.commonWarm, modifier = Modifier.weight(1f), icon = HavnIconKey.Leads)
        HavnStatCard(openHouse.stats.coldCount.toString(), strings.commonCold, modifier = Modifier.weight(1f), icon = HavnIconKey.Alert)
    }
}

@Composable
private fun OpeningLeadsCard(
    openHouse: OpenHouse,
    isLoading: Boolean,
    onOpenKiosk: () -> Unit,
    onAddQrClient: () -> Unit,
    onSelectClients: () -> Unit,
    onOpenLead: (String) -> Unit,
    onRemoveLead: (String) -> Unit,
    isSaving: Boolean
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(strings.navLeads, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                Text(
                    text = openHouse.stats.visitorCount.toString(),
                    color = theme.primaryAccent,
                    style = MaterialTheme.typography.titleMedium
                )
            }

            Text(
                strings.visitorsBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            HavnButton(strings.openKiosk, onClick = onOpenKiosk, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.QrCode)
            HavnButton(
                strings.addQrClient,
                onClick = onAddQrClient,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.QrCode
            )
            HavnButton(
                strings.addManually,
                onClick = onSelectClients,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Users
            )

            if (isLoading) {
                repeat(4) {
                    OpeningLeadSkeletonRow()
                }
            } else if (openHouse.recentSignIns.isEmpty()) {
                Text(strings.noSignInsBody, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                openHouse.recentSignIns.forEach { visitor ->
                    OpeningLeadRow(
                        visitor = visitor,
                        isSaving = isSaving,
                        onClick = { onOpenLead(visitor.id) },
                        onRemove = visitor.clientId?.let { clientId ->
                            { onRemoveLead(clientId) }
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun OpeningLeadSkeletonRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        HavnSkeletonBlock(Modifier.size(38.dp), shape = RoundedCornerShape(19.dp))
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.44f).height(14.dp))
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.68f).height(11.dp))
        }
        HavnSkeletonBlock(Modifier.width(80.dp).height(24.dp), shape = RoundedCornerShape(12.dp))
    }
}

@Composable
private fun OpeningLeadRow(
    visitor: OpenHouseVisitor,
    isSaving: Boolean,
    onClick: () -> Unit,
    onRemove: (() -> Unit)?
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        onClick = onClick,
        color = theme.surface,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = HavnSpacing.md, vertical = HavnSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    visitor.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    visitor.email?.takeIf { it.isNotBlank() } ?: strings.clientNoEmail,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            HavnStatusBadge(status = visitor.temperature.toHavnStatus())
            onRemove?.let { remove ->
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(MaterialTheme.shapes.small)
                        .clickable(enabled = !isSaving, onClick = remove),
                    contentAlignment = Alignment.Center
                ) {
                    HavnIcon(
                        icon = HavnIconKey.Trash,
                        contentDescription = strings.commonDelete,
                        tint = HavnDanger,
                        modifier = Modifier.size(18.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun VisitorKioskModeScreen(
    openHouse: OpenHouse,
    step: KioskStep,
    form: VisitorKioskFormState,
    error: String?,
    isSaving: Boolean,
    kioskPin: String,
    branding: KioskBranding,
    onChange: (VisitorKioskFormState.() -> VisitorKioskFormState) -> Unit,
    scanMessage: String?,
    recognitionFlashKey: Int,
    onQrScanned: (String) -> Unit,
    onScannerError: (String) -> Unit,
    onNext: () -> Unit,
    onBack: () -> Unit,
    onSubmit: () -> Unit,
    onExit: () -> Unit
) {
    HavnBackBlocker(enabled = true)
    HavnKioskSystemBars(active = true)
    VisitorKioskShell {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            HavnPushNavigation(
                targetState = step,
                depthFor = KioskStep::navigationDepth,
                modifier = Modifier.fillMaxSize()
            ) { visibleStep ->
                when (visibleStep) {
                    KioskStep.Welcome -> KioskWelcome(
                        openHouse = openHouse,
                        scanMessage = scanMessage,
                        recognitionFlashKey = recognitionFlashKey,
                        onQrScanned = onQrScanned,
                        onScannerError = onScannerError,
                        onNext = onNext,
                        onExit = onExit,
                        kioskPin = kioskPin,
                        branding = branding
                    )
                    KioskStep.Contact -> KioskStepCard {
                        KioskContact(form = form, error = error, onChange = onChange, onNext = onNext, onBack = onBack)
                    }
                    KioskStep.Questions -> KioskStepCard {
                        KioskQuestions(
                            form = form,
                            error = error,
                            isSaving = isSaving,
                            onChange = onChange,
                            onSubmit = onSubmit,
                            onBack = onBack
                        )
                    }
                    KioskStep.ThankYou -> KioskStepCard {
                        KioskThankYou(openHouse = openHouse)
                    }
                }
            }
        }
    }
}

@Composable
private fun KioskStepCard(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(HavnSpacing.lg),
        contentAlignment = Alignment.Center
    ) {
        HavnCard(
            modifier = Modifier
                .widthIn(max = 680.dp)
                .fillMaxWidth()
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(HavnSpacing.md),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                content()
            }
        }
    }
}

@Composable
private fun KioskWelcome(
    openHouse: OpenHouse,
    scanMessage: String?,
    recognitionFlashKey: Int,
    onQrScanned: (String) -> Unit,
    onScannerError: (String) -> Unit,
    onNext: () -> Unit,
    onExit: () -> Unit,
    kioskPin: String,
    branding: KioskBranding
) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        val landscape = maxWidth >= maxHeight
        if (landscape) {
            Row(
                modifier = Modifier.fillMaxSize()
            ) {
                KioskPropertyShowcase(
                    openHouse = openHouse,
                    compact = false,
                    modifier = Modifier.weight(2f).fillMaxHeight()
                )
                KioskCheckInShowcase(
                    openHouse = openHouse,
                    scanMessage = scanMessage,
                    recognitionFlashKey = recognitionFlashKey,
                    onQrScanned = onQrScanned,
                    onScannerError = onScannerError,
                    onNext = onNext,
                    onExit = onExit,
                    kioskPin = kioskPin,
                    branding = branding,
                    compact = false,
                    modifier = Modifier.weight(1f).fillMaxHeight()
                )
            }
        } else {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {
                KioskPropertyShowcase(
                    openHouse = openHouse,
                    compact = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(2f)
                )
                KioskCheckInShowcase(
                    openHouse = openHouse,
                    scanMessage = scanMessage,
                    recognitionFlashKey = recognitionFlashKey,
                    onQrScanned = onQrScanned,
                    onScannerError = onScannerError,
                    onNext = onNext,
                    onExit = onExit,
                    kioskPin = kioskPin,
                    branding = branding,
                    compact = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                )
            }
        }
    }
}

@Composable
private fun KioskPropertyShowcase(
    openHouse: OpenHouse,
    compact: Boolean,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(if (compact) HavnSpacing.md else HavnSpacing.lg)
    ) {
        KioskPhotoCarousel(openHouse = openHouse, compact = compact, modifier = Modifier.fillMaxSize())
    }
}

@Composable
private fun KioskPhotoCarousel(
    openHouse: OpenHouse,
    compact: Boolean,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val photos = openHouse.propertyPhotoDataUrls.ifEmpty { listOfNotNull(openHouse.propertyThumbnailDataUrl) }
    var selectedIndex by remember(openHouse.id, photos.size) { mutableStateOf(0) }
    fun nextPhoto() {
        if (photos.size > 1) selectedIndex = (selectedIndex + 1) % photos.size
    }
    fun previousPhoto() {
        if (photos.size > 1) selectedIndex = (selectedIndex - 1 + photos.size) % photos.size
    }
    LaunchedEffect(openHouse.id, photos.size, selectedIndex) {
        if (photos.size > 1) {
            delay(3_000)
            nextPhoto()
        }
    }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(if (compact) HavnSpacing.sm else HavnSpacing.md)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .background(theme.surface)
                .pointerInput(photos.size) {
                    if (photos.size > 1) {
                        var drag = 0f
                        detectHorizontalDragGestures(
                            onDragStart = { drag = 0f },
                            onHorizontalDrag = { _, amount -> drag += amount },
                            onDragEnd = {
                                when {
                                    drag < -36f -> nextPhoto()
                                    drag > 36f -> previousPhoto()
                                }
                            }
                        )
                    }
                }
        ) {
            Crossfade(
                targetState = photos.getOrNull(selectedIndex),
                animationSpec = tween(durationMillis = 520),
                label = "kiosk-photo-fade"
            ) { photoDataUrl ->
                val rendered = HavnDatabaseImage(
                    dataUrl = photoDataUrl,
                    previewDataUrl = havnPropertyPhotoPreviewSource(photoDataUrl, 220),
                    contentDescription = openHouse.propertyTitle,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnSkeletonBlock(Modifier.fillMaxSize(), shape = MaterialTheme.shapes.large)
                }
            }
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(photoTextScrim())
            )
            Column(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .fillMaxWidth()
                    .padding(if (compact) 20.dp else 28.dp),
                verticalArrangement = Arrangement.spacedBy(if (compact) HavnSpacing.sm else HavnSpacing.md)
            ) {
                Text(strings.kioskFeaturedProperty, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                Text(
                    openHouse.propertyTitle,
                    color = Color.White,
                    style = if (compact) MaterialTheme.typography.headlineLarge else MaterialTheme.typography.displaySmall,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    openHouse.propertyAddress,
                    color = Color.White.copy(alpha = 0.86f),
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)) {
                        KioskFeatureChip(icon = HavnIconKey.Home, text = strings.kioskPrivateShowing)
                        KioskFeatureChip(icon = HavnIconKey.Star, text = strings.kioskLuxuryListing)
                    }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                        KioskFeatureChip(icon = HavnIconKey.Home, text = strings.kioskPrivateShowing)
                        KioskFeatureChip(icon = HavnIconKey.Star, text = strings.kioskLuxuryListing)
                        KioskFeatureChip(icon = HavnIconKey.QrCode, text = strings.kioskFastCheckIn)
                    }
                }
                KioskCarouselDots(count = photos.size.coerceAtLeast(1), selectedIndex = selectedIndex)
            }
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = if (compact) HavnSpacing.sm else HavnSpacing.md, vertical = HavnSpacing.sm),
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            val thumbnailPhotos = photos.take(5).ifEmpty { List(1) { null } }
            thumbnailPhotos.forEachIndexed { index, dataUrl ->
                KioskThumbnail(
                    dataUrl = dataUrl,
                    selected = index == selectedIndex,
                    modifier = Modifier.weight(1f),
                    onClick = {
                        selectedIndex = index
                    }
                )
            }
        }
    }
}

@Composable
private fun KioskCarouselArrow(
    direction: HavnIconKey,
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.size(54.dp),
        shape = MaterialTheme.shapes.extraLarge,
        color = Color(0xBB05080D),
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(
                direction,
                contentDescription = null,
                tint = theme.primaryAccent,
                modifier = Modifier
                    .size(28.dp)
                    .clickable(onClick = onClick)
            )
        }
    }
}

@Composable
private fun KioskCarouselDots(count: Int, selectedIndex: Int) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
        repeat(count.coerceIn(1, 8)) { index ->
            Box(
                modifier = Modifier
                    .width(if (index == selectedIndex) 24.dp else 8.dp)
                    .height(8.dp)
                    .clip(MaterialTheme.shapes.small)
                    .background(if (index == selectedIndex) theme.primaryAccent else theme.mutedText.copy(alpha = 0.58f))
            )
        }
    }
}

@Composable
private fun KioskCheckInShowcase(
    openHouse: OpenHouse,
    scanMessage: String?,
    recognitionFlashKey: Int,
    onQrScanned: (String) -> Unit,
    onScannerError: (String) -> Unit,
    onNext: () -> Unit,
    onExit: () -> Unit,
    kioskPin: String,
    branding: KioskBranding,
    compact: Boolean,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    var showExitPin by remember { mutableStateOf(false) }
    var enteredPin by remember { mutableStateOf("") }
    var pinError by remember { mutableStateOf(false) }
    val showQrScanner = !havnIsWebShell
    val recognitionFlash = remember { Animatable(0f) }
    LaunchedEffect(recognitionFlashKey) {
        if (recognitionFlashKey > 0) {
            playHavnKioskRecognitionBell()
            recognitionFlash.snapTo(1f)
            recognitionFlash.animateTo(0f, animationSpec = tween(durationMillis = 1200))
        }
    }
    val flashAmount = recognitionFlash.value
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.extraLarge,
        color = Color(
            red = theme.card.red + (1f - theme.card.red) * flashAmount,
            green = theme.card.green + (1f - theme.card.green) * flashAmount,
            blue = theme.card.blue + (1f - theme.card.blue) * flashAmount,
            alpha = 0.93f + 0.07f * flashAmount
        ),
        border = BorderStroke(
            if (flashAmount > 0f) 4.dp else 1.dp,
            if (flashAmount > 0f) {
                theme.primaryAccent.copy(alpha = 0.5f + flashAmount * 0.5f)
            } else {
                theme.border
            }
        )
    ) {
        BoxWithConstraints(Modifier.fillMaxSize()) {
            if (flashAmount > 0f) {
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .background(Color.White.copy(alpha = flashAmount * 0.42f))
                )
            }
            val tight = compact || maxHeight < 430.dp || maxWidth < 320.dp
            val qrMax = when {
                maxHeight < 330.dp -> 92.dp
                tight -> minOf(maxWidth * 0.42f, maxHeight * 0.48f, 220.dp)
                else -> 250.dp
            }
            val veryTight = maxHeight < 330.dp
            val contentPadding = when {
                veryTight -> 8.dp
                tight -> 14.dp
                else -> 30.dp
            }
            Surface(
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(contentPadding)
                    .size(if (veryTight) 34.dp else 42.dp)
                    .clickable {
                        enteredPin = ""
                        pinError = false
                        showExitPin = true
                    },
                shape = MaterialTheme.shapes.large,
                color = theme.background.copy(alpha = 0.52f),
                border = BorderStroke(1.dp, theme.border)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    HavnIcon(
                        HavnIconKey.Back,
                        contentDescription = strings.kioskExitContentDescription,
                        tint = theme.primaryAccent,
                        modifier = Modifier.size(if (veryTight) 18.dp else 22.dp)
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(contentPadding),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth(if (havnIsWebShell) 0.78f else 1f)
                        .widthIn(max = if (tight) 360.dp else 480.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(if (tight) 6.dp else HavnSpacing.sm)
                ) {
                    if (!tight) {
                        KioskBrand(
                            branding = branding,
                            centered = true
                        )
                    }
                    Text(
                        strings.kioskWelcome,
                        color = theme.primaryText,
                        style = if (tight) MaterialTheme.typography.headlineLarge else MaterialTheme.typography.displaySmall,
                        textAlign = TextAlign.Center,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (!tight) {
                        Text(strings.kioskGladHere, color = theme.primaryAccent, style = MaterialTheme.typography.titleLarge, textAlign = TextAlign.Center)
                        Box(Modifier.width(64.dp).height(2.dp).background(theme.primaryAccent, MaterialTheme.shapes.small))
                    }
                    if (showQrScanner) {
                        Text(
                            strings.kioskScanToCheckIn,
                            color = theme.primaryAccent,
                            style = if (veryTight) MaterialTheme.typography.labelLarge else MaterialTheme.typography.titleMedium,
                            textAlign = TextAlign.Center
                        )
                        if (!tight) {
                            Text(strings.kioskReceivePropertyDetails, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge, textAlign = TextAlign.Center)
                        }
                    } else if (!tight) {
                        Text(
                            strings.kioskSecureInfo,
                            color = theme.primaryText,
                            style = MaterialTheme.typography.bodyLarge,
                            textAlign = TextAlign.Center
                        )
                    }
                }

                if (showQrScanner) {
                    Spacer(Modifier.weight(if (tight) 0.35f else 0.5f))
                } else {
                    Spacer(Modifier.height(if (tight) 72.dp else 96.dp))
                }

                if (showQrScanner) {
                    Surface(
                        modifier = Modifier.widthIn(max = qrMax).fillMaxWidth(),
                        shape = MaterialTheme.shapes.large,
                        color = theme.background,
                        border = BorderStroke(4.dp, theme.primaryAccent)
                    ) {
                        Box(
                            Modifier
                                .aspectRatio(1f)
                                .clip(MaterialTheme.shapes.medium),
                            contentAlignment = Alignment.Center
                        ) {
                            HavnQrScannerPreview(
                                active = true,
                                cameraFacing = HavnCameraFacing.Front,
                                onQrScanned = onQrScanned,
                                onError = onScannerError,
                                modifier = Modifier.fillMaxSize()
                            )
                            Surface(
                                color = theme.background,
                                shape = MaterialTheme.shapes.small,
                                border = BorderStroke(1.dp, theme.primaryAccent)
                            ) {
                                Text("H", modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp), color = theme.primaryAccent, style = MaterialTheme.typography.titleLarge)
                            }
                        }
                    }
                }

                scanMessage?.takeIf { showQrScanner }?.let {
                    Text(it, color = theme.primaryAccent, style = MaterialTheme.typography.bodyMedium, textAlign = TextAlign.Center)
                }

                if (showQrScanner) {
                    Spacer(Modifier.weight(if (tight) 0.25f else 0.4f))
                } else {
                    Spacer(Modifier.height(if (tight) 28.dp else 40.dp))
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth(if (havnIsWebShell) 0.72f else 1f)
                        .widthIn(max = if (tight) 420.dp else 560.dp)
                        .navigationBarsPadding(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(if (tight) HavnSpacing.sm else HavnSpacing.md)
                ) {
                    if (showQrScanner) {
                        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                            Box(Modifier.weight(1f).height(1.dp).background(theme.primaryAccent.copy(alpha = 0.35f)))
                            Text(strings.kioskOr, modifier = Modifier.padding(horizontal = 16.dp), color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                            Box(Modifier.weight(1f).height(1.dp).background(theme.primaryAccent.copy(alpha = 0.35f)))
                        }
                    }
                    HavnButton(
                        text = strings.kioskStartCheckInManually,
                        onClick = onNext,
                        modifier = Modifier.fillMaxWidth().heightIn(min = if (veryTight) 42.dp else if (tight) 48.dp else 64.dp),
                        leadingIcon = HavnIconKey.User
                    )
                    if (!tight) {
                        Text("HAVN · ${strings.kioskSecureInfo}", color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, textAlign = TextAlign.Center)
                    }
                }
            }
        }
    }
    if (showExitPin) {
        AlertDialog(
            onDismissRequest = { showExitPin = false },
            containerColor = theme.card,
            title = { Text(strings.kioskExitTitle, color = theme.primaryText) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                    Text(strings.kioskExitBody, color = theme.secondaryText)
                    KioskPinPad(
                        value = enteredPin,
                        onDigit = { digit ->
                            enteredPin = (enteredPin + digit).take(8)
                            pinError = false
                        },
                        onBackspace = {
                            enteredPin = enteredPin.dropLast(1)
                            pinError = false
                        }
                    )
                    if (pinError) {
                        Text(strings.kioskIncorrectPin, color = HavnDanger, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    if (enteredPin == kioskPin) {
                        showExitPin = false
                        onExit()
                    } else {
                        pinError = true
                    }
                }) {
                    Text(strings.kioskUnlock, color = theme.primaryAccent)
                }
            },
            dismissButton = {
                TextButton(onClick = { showExitPin = false }) {
                    Text(strings.commonCancel, color = theme.secondaryText)
                }
            }
        )
    }
}

@Composable
private fun KioskPinPad(
    value: String,
    onDigit: (String) -> Unit,
    onBackspace: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp),
            color = theme.background,
            shape = MaterialTheme.shapes.medium,
            border = BorderStroke(1.dp, theme.primaryAccent)
        ) {
            Box(contentAlignment = Alignment.CenterStart, modifier = Modifier.padding(horizontal = HavnSpacing.md)) {
                Text(
                    text = if (value.isBlank()) "PIN" else value.map { "*" }.joinToString(" "),
                    color = if (value.isBlank()) theme.secondaryText else theme.primaryText,
                    style = MaterialTheme.typography.titleLarge
                )
            }
        }

        listOf(
            listOf("1", "2", "3"),
            listOf("4", "5", "6"),
            listOf("7", "8", "9"),
            listOf("", "0", "Del")
        ).forEach { row ->
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                row.forEach { key ->
                    if (key.isBlank()) {
                        Spacer(Modifier.weight(1f))
                    } else {
                        Surface(
                            modifier = Modifier
                                .weight(1f)
                                .height(50.dp)
                                .clip(MaterialTheme.shapes.medium)
                                .clickable {
                                    if (key == "Del") onBackspace() else onDigit(key)
                                },
                            color = theme.surface,
                            shape = MaterialTheme.shapes.medium,
                            border = BorderStroke(1.dp, theme.border)
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(
                                    text = key,
                                    color = theme.primaryText,
                                    style = MaterialTheme.typography.titleMedium
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun KioskBrand(
    branding: KioskBranding,
    modifier: Modifier = Modifier,
    centered: Boolean = false
) {
    val theme = LocalHavnThemeTokens.current
    @Composable
    fun BrandIcon(size: androidx.compose.ui.unit.Dp) {
        if (branding.useAgencyBranding && !branding.agencyIconUrl.isNullOrBlank()) {
            val rendered = HavnDatabaseImage(
                dataUrl = branding.agencyIconUrl,
                previewDataUrl = havnAgencyMediaPreviewSource(branding.agencyIconUrl, size.value.toInt().coerceAtLeast(64)),
                contentDescription = branding.title,
                modifier = Modifier
                    .size(size)
                    .clip(MaterialTheme.shapes.medium),
                contentScale = ContentScale.Crop
            )
            if (!rendered) {
                HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(size * 0.82f))
            }
        } else {
            HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(size * 0.82f))
        }
    }

    if (!centered) {
        Row(
            modifier = modifier,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
            verticalAlignment = Alignment.CenterVertically
        ) {
            BrandIcon(44.dp)
            Column(verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Text(
                    branding.title,
                    color = theme.primaryAccent,
                    style = MaterialTheme.typography.displaySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    branding.subtitle,
                    color = theme.primaryAccent,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
        return
    }

    val horizontalAlignment = if (centered) Alignment.CenterHorizontally else Alignment.Start
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(4.dp),
        horizontalAlignment = horizontalAlignment
    ) {
        BrandIcon(44.dp)
        Text(
            branding.title,
            color = theme.primaryAccent,
            style = MaterialTheme.typography.displaySmall,
            textAlign = if (centered) TextAlign.Center else TextAlign.Start,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        Text(
            branding.subtitle,
            color = theme.primaryAccent,
            style = MaterialTheme.typography.labelMedium,
            textAlign = if (centered) TextAlign.Center else TextAlign.Start,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun KioskMetric(icon: HavnIconKey, value: String, label: String) {
    val theme = LocalHavnThemeTokens.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
        HavnIcon(icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(24.dp))
        Column {
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.titleMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        }
    }
}

@Composable
private fun KioskFeatureChip(icon: HavnIconKey, text: String) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.background.copy(alpha = 0.54f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 10.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            Text(text, color = theme.primaryText, style = MaterialTheme.typography.labelMedium)
        }
    }
}

@Composable
private fun KioskThumbnail(
    dataUrl: String?,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .height(78.dp)
            .clickable(enabled = dataUrl != null, onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(if (selected) 2.dp else 1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            val rendered = HavnDatabaseImage(
                dataUrl = dataUrl,
                previewDataUrl = havnPropertyPhotoPreviewSource(dataUrl, 120),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
            if (!rendered) {
                HavnSkeletonBlock(Modifier.fillMaxSize(), shape = MaterialTheme.shapes.medium)
                HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
            }
        }
    }
}

@Composable
private fun KioskContact(
    form: VisitorKioskFormState,
    error: String?,
    onChange: (VisitorKioskFormState.() -> VisitorKioskFormState) -> Unit,
    onNext: () -> Unit,
    onBack: () -> Unit
) {
    val strings = LocalHavnStrings.current
    KioskStepTitle(strings.kioskTellUsTitle, strings.kioskTellUsBody)
    error?.let { Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.titleMedium) }
    HavnTextField(form.fullName, { onChange { copy(fullName = it) } }, strings.kioskFullName, leadingIcon = HavnIconKey.User)
    HavnTextField(
        form.phone,
        { onChange { copy(phone = it) } },
        strings.phone,
        leadingIcon = HavnIconKey.Phone,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
    )
    HavnTextField(
        form.email,
        { onChange { copy(email = it) } },
        strings.email,
        leadingIcon = HavnIconKey.Mail,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
    )
    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
        HavnButton(strings.back, onClick = onBack, modifier = Modifier.weight(1f).heightIn(min = 64.dp), style = HavnButtonStyle.Ghost)
        HavnButton(strings.kioskContinue, onClick = onNext, modifier = Modifier.weight(1f).heightIn(min = 64.dp))
    }
}

@Composable
private fun KioskQuestions(
    form: VisitorKioskFormState,
    error: String?,
    isSaving: Boolean,
    onChange: (VisitorKioskFormState.() -> VisitorKioskFormState) -> Unit,
    onSubmit: () -> Unit,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    KioskStepTitle(strings.kioskQuestionsTitle, strings.kioskQuestionsBody)
    error?.let { Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.titleMedium) }
    Text(strings.kioskWorkingWithAgentQuestion, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
        KioskChoice(strings.kioskYes, selected = form.workingWithAgent == true, modifier = Modifier.weight(1f)) {
            onChange { copy(workingWithAgent = true) }
        }
        KioskChoice(strings.kioskNo, selected = form.workingWithAgent == false, modifier = Modifier.weight(1f)) {
            onChange { copy(workingWithAgent = false) }
        }
    }
    Text(strings.kioskInterestQuestion, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
    VisitorInterest.entries.chunked(2).forEach { row ->
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            row.forEach { interest ->
                KioskChoice(interest.localizedLabel(strings), selected = form.interest == interest, modifier = Modifier.weight(1f)) {
                    onChange { copy(interest = interest) }
                }
            }
            if (row.size == 1) Spacer(Modifier.weight(1f))
        }
    }
    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
        HavnButton(strings.back, onClick = onBack, modifier = Modifier.weight(1f).heightIn(min = 64.dp), style = HavnButtonStyle.Ghost)
        HavnButton(strings.kioskFinish, onClick = onSubmit, modifier = Modifier.weight(1f).heightIn(min = 64.dp), loading = isSaving)
    }
}

@Composable
private fun KioskThankYou(openHouse: OpenHouse) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(180.dp)
            .background(Brush.linearGradient(listOf(theme.secondaryAccent, theme.card)), MaterialTheme.shapes.extraLarge),
        contentAlignment = Alignment.Center
    ) {
        Text(strings.kioskThankYou, color = theme.primaryText, style = MaterialTheme.typography.displaySmall)
    }
    Text("${strings.kioskEnjoyVisitPrefix} ${openHouse.propertyTitle}.", color = theme.secondaryText, style = MaterialTheme.typography.titleLarge, textAlign = TextAlign.Center)
    Text(strings.kioskResetAutomatically, color = theme.mutedText, style = MaterialTheme.typography.bodyLarge, textAlign = TextAlign.Center)
}

private fun VisitorInterest.localizedLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    VisitorInterest.Buying -> strings.kioskInterestBuying
    VisitorInterest.Investing -> strings.kioskInterestInvesting
    VisitorInterest.Browsing -> strings.kioskInterestBrowsing
    VisitorInterest.Relocating -> strings.kioskInterestRelocating
}

@Composable
private fun KioskStepTitle(title: String, subtitle: String) {
    val theme = LocalHavnThemeTokens.current
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Text(title, color = theme.primaryText, style = MaterialTheme.typography.headlineMedium, textAlign = TextAlign.Center)
        Text(subtitle, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge, textAlign = TextAlign.Center)
    }
}

@Composable
private fun KioskChoice(text: String, selected: Boolean, modifier: Modifier = Modifier, onClick: () -> Unit) {
    HavnButton(
        text = text,
        onClick = onClick,
        modifier = modifier.heightIn(min = 68.dp),
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary
    )
}

@Composable
private fun PropertyHero(openHouse: OpenHouse) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(220.dp)
            .background(
                Brush.linearGradient(
                    listOf(theme.primaryText, theme.primaryAccent, theme.surface)
                ),
                MaterialTheme.shapes.extraLarge
            ),
        contentAlignment = Alignment.BottomStart
    ) {
        Column(Modifier.fillMaxWidth().background(Color(0x9905080D)).fillMaxWidth()) {
            Text(
                openHouse.propertyTitle,
                modifier = Modifier.fillMaxWidth(),
                color = Color.White,
                style = MaterialTheme.typography.headlineSmall,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun KioskPanel(
    onOpenKiosk: () -> Unit,
    onAddQrClient: () -> Unit,
    onSelectClients: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(strings.commonVisitors, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            Text(
                strings.visitorsBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            HavnButton(strings.openKiosk, onClick = onOpenKiosk, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.QrCode)
            HavnButton(
                strings.addQrClient,
                onClick = onAddQrClient,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.QrCode
            )
            HavnButton(
                strings.addManually,
                onClick = onSelectClients,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Users
            )
        }
    }
}

@Composable
private fun ScanClientQrScreen(
    openHouse: OpenHouse,
    payload: String,
    payloads: List<String>,
    message: String?,
    error: String?,
    isSaving: Boolean,
    onPayloadChange: (String) -> Unit,
    onPayloadScanned: (String) -> Unit,
    onRemovePayload: (String) -> Unit,
    onSubmit: () -> Unit,
    onBack: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var scannerError by remember { mutableStateOf<String?>(null) }
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(strings.kioskCheckIn, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            message?.let { Text(it, color = theme.primaryAccent, style = MaterialTheme.typography.bodyMedium) }
            error?.let { Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.bodyMedium) }
            scannerError?.let { Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.bodyMedium) }
            QrImageCheckInPanel(
                payload = payload,
                payloads = payloads,
                previewPayload = kioskPreviewPayload(openHouse),
                onPayloadScanned = onPayloadScanned,
                onScannerError = { scannerError = it },
                onRemovePayload = onRemovePayload,
                onSubmit = onSubmit,
                isSaving = isSaving
            )
        }
    }
}

@Composable
private fun SelectClientsScreen(
    openHouse: OpenHouse,
    clients: List<Client>,
    clientSearchQuery: String,
    message: String?,
    error: String?,
    isSaving: Boolean,
    isLoadingClients: Boolean,
    isLoadingMoreClients: Boolean,
    selectedTemperature: LeadTemperature,
    onClientSearchChange: (String) -> Unit,
    onTemperatureSelected: (LeadTemperature) -> Unit,
    onClientSelected: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(strings.selectClients, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            message?.let { Text(it, color = theme.primaryAccent, style = MaterialTheme.typography.bodyMedium) }
            error?.let { Text(it, color = com.havn.app.core.designsystem.HavnDanger, style = MaterialTheme.typography.bodyMedium) }
            HavnSearchField(
                value = clientSearchQuery,
                onValueChange = onClientSearchChange,
                placeholder = strings.searchClients
            )
            ManualLeadTemperatureSelector(
                selectedTemperature = selectedTemperature,
                enabled = !isSaving,
                onTemperatureSelected = onTemperatureSelected
            )
            if (isLoadingClients && clients.isEmpty()) {
                repeat(5) {
                    ManualClientCheckInSkeletonRow()
                }
            } else if (clients.isEmpty()) {
                Text(strings.noClientsFoundShort, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                clients.forEach { client ->
                    val isAssigned = openHouse.recentSignIns.any { it.clientId == client.id }
                    ManualClientCheckInRow(
                        client = client,
                        isAssigned = isAssigned,
                        isSaving = isSaving,
                        onClick = {
                            if (!isAssigned) {
                                onClientSelected(client.id)
                            }
                        }
                    )
                }
                if (isLoadingMoreClients) {
                    HavnSkeletonListFooter()
                }
            }
        }
    }
}

@Composable
private fun ManualClientCheckInSkeletonRow() {
    HavnCard(border = BorderStroke(1.dp, HavnCardBorder)) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnSkeletonBlock(Modifier.size(40.dp), shape = RoundedCornerShape(20.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.46f).height(14.dp))
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.66f).height(11.dp))
            }
            HavnSkeletonBlock(Modifier.width(78.dp).height(28.dp), shape = RoundedCornerShape(14.dp))
        }
    }
}

@Composable
private fun ManualLeadTemperatureSelector(
    selectedTemperature: LeadTemperature,
    enabled: Boolean,
    onTemperatureSelected: (LeadTemperature) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(3.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            LeadTemperature.entries.forEach { temperature ->
                ManualLeadTemperatureChip(
                    label = temperature.localizedManualLabel(strings),
                    temperature = temperature,
                    selected = selectedTemperature == temperature,
                    onClick = { onTemperatureSelected(temperature) },
                    modifier = Modifier.weight(1f),
                    enabled = enabled
                )
            }
        }
    }
}

@Composable
private fun ManualLeadTemperatureChip(
    label: String,
    temperature: LeadTemperature,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val accent = temperature.temperatureAccent()
    val background = if (selected) accent else accent.copy(alpha = 0.16f)
    val contentColor = if (selected) temperature.temperatureContentColor(theme.primaryText) else accent
    Surface(
        shape = RoundedCornerShape(9.dp),
        color = if (enabled) background else theme.card,
        border = BorderStroke(1.dp, if (selected) contentColor.copy(alpha = 0.55f) else accent.copy(alpha = 0.46f)),
        modifier = modifier
            .height(HavnListDefaults.filterChipHeight)
            .clip(RoundedCornerShape(9.dp))
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = label,
                color = if (enabled) contentColor else theme.mutedText,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun QrImageCheckInPanel(
    payload: String,
    payloads: List<String>,
    previewPayload: String,
    onPayloadScanned: (String) -> Unit,
    onScannerError: (String) -> Unit,
    onRemovePayload: (String) -> Unit,
    onSubmit: () -> Unit,
    isSaving: Boolean
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(1f)
                .background(
                    Brush.linearGradient(listOf(theme.gradientStart, theme.surface, theme.gradientEnd)),
                    MaterialTheme.shapes.large
                )
                .padding(18.dp),
            contentAlignment = Alignment.Center
        ) {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = theme.surface,
                shape = MaterialTheme.shapes.large,
                border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.55f))
            ) {
                Box(Modifier.fillMaxSize()) {
                    HavnQrScannerPreview(
                        active = !isSaving,
                        onQrScanned = onPayloadScanned,
                        onError = onScannerError,
                        modifier = Modifier.fillMaxSize()
                    )
                    Box(
                        modifier = Modifier
                            .align(Alignment.Center)
                            .size(188.dp)
                            .background(Color.Transparent)
                            .clip(MaterialTheme.shapes.large)
                            .border(2.dp, theme.primaryAccent.copy(alpha = 0.82f), MaterialTheme.shapes.large)
                    )
                    Text(
                        strings.clientQrImageBody,
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .fillMaxWidth()
                            .background(Color(0xAA05080D))
                            .padding(12.dp),
                        color = theme.primaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        textAlign = TextAlign.Center
                    )
                }
            }
        }

        if (payloads.isEmpty()) {
            Text(
                strings.scanMultipleQrBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)) {
                Text("${payloads.size} ${strings.scannedQrReady}", color = theme.primaryAccent, style = MaterialTheme.typography.titleSmall)
                payloads.forEachIndexed { index, scannedPayload ->
                    PendingQrPayloadRow(
                        index = index,
                        payload = scannedPayload,
                        onRemove = { onRemovePayload(scannedPayload) }
                    )
                }
            }
        }
        HavnButton(
            text = strings.addQrClient,
            onClick = onSubmit,
            modifier = Modifier.fillMaxWidth(),
            enabled = payloads.isNotEmpty() || payload.isNotBlank(),
            loading = isSaving,
            leadingIcon = HavnIconKey.Check
        )
    }
}

@Composable
private fun PendingQrPayloadRow(
    index: Int,
    payload: String,
    onRemove: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val label = HavnClientQrPayload.decode(payload)
        .getOrNull()
        ?.clientId
        ?.takeLast(8)
        ?.let { "Contact $it" }
        ?: "Contact QR ${index + 1}"
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.background.copy(alpha = 0.42f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            HavnIcon(HavnIconKey.QrCode, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            Text(label, modifier = Modifier.weight(1f), color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            HavnButton(
                text = LocalHavnStrings.current.remove,
                onClick = onRemove,
                style = HavnButtonStyle.Ghost
            )
        }
    }
}

private fun kioskPreviewPayload(openHouse: OpenHouse): String =
    """{"sourceApp":"HAVN","version":1,"openingId":"${openHouse.id}"}"""

@Composable
private fun ManualClientCheckInRow(
    client: Client,
    isAssigned: Boolean,
    isSaving: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        onClick = onClick,
        enabled = !isSaving && !isAssigned,
        color = theme.background.copy(alpha = 0.35f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(HavnIconKey.User, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(client.fullName, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(client.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            Text(
                if (isAssigned) strings.commonCheckedIn else strings.commonAdd,
                color = if (isAssigned) theme.secondaryText else theme.primaryAccent,
                style = MaterialTheme.typography.labelLarge
            )
        }
    }
}

@Composable
private fun ErrorPanel(error: String, onDismiss: () -> Unit, onUpgradePlan: () -> Unit = {}) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(error, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
            if (error.isUpgradePrompt()) {
                HavnButton(strings.upgradePlan, onClick = onUpgradePlan, modifier = Modifier.fillMaxWidth(), style = HavnButtonStyle.Primary)
            }
            HavnButton(strings.commonDismiss, onClick = onDismiss, modifier = Modifier.fillMaxWidth(), style = HavnButtonStyle.Ghost)
        }
    }
}

private fun String.isUpgradePrompt(): Boolean {
    val normalized = lowercase()
    return "upgrade" in normalized || "current plan" in normalized || "limit reached" in normalized || "storage limit" in normalized
}

private enum class OpeningTimelineStatus {
    Active,
    Planned,
    Archived
}

@OptIn(ExperimentalTime::class)
private fun OpenHouse.timelineStatus(nowIso: String = Clock.System.now().toString()): OpeningTimelineStatus {
    val now = nowIso.timelineComparable()
    val start = startsAt.timelineComparable()
    val end = endsAt.timelineComparable()
    return when {
        start.isNotBlank() && now < start -> OpeningTimelineStatus.Planned
        end.isNotBlank() && now > end -> OpeningTimelineStatus.Archived
        else -> OpeningTimelineStatus.Active
    }
}

private fun OpenHouse.toPropertyOption(): OpenHousePropertyOption = OpenHousePropertyOption(
    id = propertyId,
    title = propertyTitle,
    address = propertyAddress,
    thumbnailDataUrl = propertyThumbnailDataUrl,
    photoDataUrls = propertyPhotoDataUrls
)

private fun String.timelineComparable(): String =
    trim()
        .replace(" ", "T")
        .substringBefore(".")
        .substringBefore("+")
        .removeSuffix("Z")

private fun LeadTemperature.toHavnStatus(): HavnStatus = when (this) {
    LeadTemperature.Hot -> HavnStatus.Hot
    LeadTemperature.Warm -> HavnStatus.Warm
    LeadTemperature.Cold -> HavnStatus.Cold
}

private fun LeadTemperature.temperatureAccent(): Color = when (this) {
    LeadTemperature.Hot -> Color(0xFFE85D4A)
    LeadTemperature.Warm -> Color(0xFFD99A32)
    LeadTemperature.Cold -> Color(0xFF35C9D0)
}

private fun LeadTemperature.temperatureContentColor(defaultTextColor: Color): Color =
    if (this == LeadTemperature.Cold) Color(0xFF031316) else defaultTextColor

private fun LeadTemperature.localizedManualLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    LeadTemperature.Hot -> strings.commonHot
    LeadTemperature.Warm -> strings.commonWarm
    LeadTemperature.Cold -> strings.commonCold
}

private fun OpenHousesMode.navigationDepth(): Int = when (this) {
    OpenHousesMode.List -> 0
    OpenHousesMode.Detail,
    OpenHousesMode.Schedule,
    OpenHousesMode.Edit -> 1
    OpenHousesMode.LiveDashboard,
    OpenHousesMode.ScanQr,
    OpenHousesMode.SelectClients -> 2
    OpenHousesMode.VisitorKiosk -> 3
}

private fun KioskStep.navigationDepth(): Int = when (this) {
    KioskStep.Welcome -> 0
    KioskStep.Contact -> 1
    KioskStep.Questions -> 2
    KioskStep.ThankYou -> 3
}
